import { Link } from '@tanstack/react-router'
import { useState } from 'react'

import { ChevronLeftIcon } from '@/components/icons'
import { cx } from '@/lib/cx'
import { useT } from '@/lib/i18n/translate'
import {
  NAV_GROUP_LABELS,
  type NavTarget,
  navSections,
  readSidebarCollapsed,
  writeSidebarCollapsed,
} from '@/lib/nav'

/** Large screens (T13): persistent sidebar, collapsible to an icon rail with tooltips. */
export function Sidebar({ items }: { items: readonly NavTarget[] }) {
  const t = useT()
  const [collapsed, setCollapsed] = useState(readSidebarCollapsed)
  if (items.length === 0) return null

  const toggle = () => {
    writeSidebarCollapsed(!collapsed)
    setCollapsed(!collapsed)
  }

  return (
    <nav
      aria-label={t('nav.label')}
      className={cx(
        'bg-surface border-border sticky top-14 hidden h-[calc(100dvh-3.5rem)] flex-col overflow-y-auto border-r p-2 md:flex print:hidden',
        collapsed ? 'w-16' : 'w-60',
      )}
    >
      <div className="flex-1">
        {navSections(items).map((section) => (
          <div key={section.group ?? 'top'}>
            {section.group && !collapsed && (
              <p className="text-muted px-3 pt-3 pb-1 text-xs font-medium">{t(NAV_GROUP_LABELS[section.group])}</p>
            )}
            {section.items.map((item) => (
              <Link
                key={item.key}
                to={item.to}
                params={item.params ?? {}}
                title={collapsed ? t(item.label) : undefined}
                activeOptions={{ exact: item.exact ?? false }}
                activeProps={{ className: 'bg-accent text-accent-fg' }}
                inactiveProps={{ className: 'hover:bg-accent-soft' }}
                className={cx('flex min-h-tap items-center gap-3 rounded-lg', collapsed ? 'justify-center' : 'px-3')}
              >
                <item.icon />
                {!collapsed && <span className="truncate">{t(item.label)}</span>}
              </Link>
            ))}
          </div>
        ))}
      </div>
      <button
        type="button"
        aria-label={t(collapsed ? 'nav.expand' : 'nav.collapse')}
        title={collapsed ? t('nav.expand') : undefined}
        onClick={toggle}
        className={cx(
          'text-muted hover:bg-accent-soft flex min-h-tap items-center gap-3 rounded-lg',
          collapsed ? 'justify-center' : 'px-3',
        )}
      >
        <ChevronLeftIcon className={cx(collapsed && 'rotate-180')} />
        {!collapsed && <span>{t('nav.collapse')}</span>}
      </button>
    </nav>
  )
}
